import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LogIn, Loader2, CheckCircle } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { PasswordField } from "@/components/auth/PasswordField";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import HubfloBanner from "@/components/ui/HubfloBanner";

const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
});

type LoginValues = z.infer<typeof loginSchema>;

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state, or default to "/"
  const from = location.state?.from?.pathname || "/";

  // Get success message from location state (e.g., from password reset)
  const successMessage = location.state?.message;

  // Check if there's an invitation email in sessionStorage (from email mismatch redirect)
  const invitationEmail = sessionStorage.getItem("invitationEmail");
  const hasInvitation = !!invitationEmail;

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: invitationEmail || "",
      password: "",
    },
  });

  // If user is already logged in, don't render login form - AuthContext will handle redirect
  if (user) {
    console.log(
      "[Login] User already authenticated, AuthContext will handle redirect",
    );
    return null;
  }

  const onSubmit = async (values: LoginValues) => {
    setIsLoading(true);

    try {
      const { error } = await signIn(values.email, values.password);

      if (!error) {
        // Don't redirect here - let AuthContext handle it based on user state
        console.log("Login successful. AuthContext will handle redirect.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Reusable banner component */}
      <HubfloBanner showAnimation={true} />

      {/* Content section with proper padding */}
      <div className="flex-1 flex flex-col items-center p-4 pt-8">
        <div className="max-w-md w-full mx-auto pb-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold">Welcome back</h1>
            <p className="text-gray-600 mt-2 text-sm">
              Sign in to your account to access your AI Agents
            </p>
          </div>

          {/* Success message (e.g., from password reset) */}
          {successMessage && (
            <Alert className="mb-6 bg-green-50 border-green-200 text-green-800">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          {/* Invitation notice */}
          {hasInvitation && (
            <Alert className="mb-6 bg-blue-50 border-blue-200 text-blue-800">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                You're signing in to accept a team invitation. Please sign in
                with <strong>{invitationEmail}</strong> to continue.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 mb-6">
            <GoogleSignInButton buttonText="Sign in with Google" />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">OR</span>
              </div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="you@example.com"
                        {...field}
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <PasswordField
                form={form}
                name="password"
                label="Password"
                disabled={isLoading}
              />

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#ecb22d] hover:text-[#d9a429] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </Form>

          <div className="mt-8 text-center">
            <div className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-[#ecb22d] hover:text-[#d9a429] hover:underline font-medium"
              >
                Sign up
              </Link>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-gray-500">
            By signing up, you agree to our{" "}
            <a
              href="https://www.hubflo.com/utility-pages/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 hover:underline"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://www.hubflo.com/utility-pages/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 hover:underline"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
